import { useEffect, useMemo, useRef, useState } from 'react'
import { MessageCircle, Send, X } from 'lucide-react'
import type { Customer } from '../api/types'
import { usePublicAgentChat } from '../hooks/usePublicAgentChat'

export function PublicChatWidget({ customer }: { customer: Customer | null }) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const { messages, isLoading, isSending, error, sendMessage, suggestPhone } = usePublicAgentChat(customer)
  const chatBodyRef = useRef<HTMLDivElement>(null)

  const visibleMessages = useMemo(() => messages.slice(-20), [messages])

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight
    }
  }, [visibleMessages, isSending])

  const handleSubmit = async () => {
    const next = input.trim()
    if (!next || isSending) return
    setInput('')
    await sendMessage(next)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <>
      <button className="chat-fab" onClick={() => setOpen((prev) => !prev)} aria-label="Abrir chat">
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <section className="chat-widget">
          <header className="chat-header">
            <div>
              <strong>Asistente de Reservas</strong>
              <p>Consulta hotel, disponibilidad y reservas.</p>
            </div>
            <button className="chat-close" onClick={() => setOpen(false)} aria-label="Cerrar chat">
              <X size={18} />
            </button>
          </header>

          <div className="chat-body" ref={chatBodyRef}>
            {isLoading && <div className="chat-state">Cargando chat...</div>}
            {!isLoading &&
              visibleMessages.map((message) => (
                <article key={message.id} className={`chat-bubble ${message.sender_type === 'guest' ? 'guest' : 'assistant'}`}>
                  <p>{message.content}</p>
                </article>
              ))}
            {isSending && (
              <article className="chat-bubble assistant typing">
                <span className="typing-dots">
                  <span>.</span><span>.</span><span>.</span>
                </span>
              </article>
            )}
            {!isLoading && visibleMessages.length <= 1 && (
              <div className="chat-quick-actions">
                {customer?.phone && (
                  <button className="secondary-button" onClick={suggestPhone} disabled={isSending}>
                    Usar mi número
                  </button>
                )}
                <button className="secondary-button" onClick={() => setInput('Quiero consultar disponibilidad')}>
                  Consultar disponibilidad
                </button>
              </div>
            )}
            {error && <div className="chat-state error">{error}</div>}
          </div>

          <footer className="chat-footer">
            <textarea
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu mensaje..."
              disabled={isSending}
            />
            <button className="primary-button chat-send" onClick={handleSubmit} disabled={isSending || !input.trim()}>
              <Send size={16} />
              <span>{isSending ? 'Enviando...' : 'Enviar'}</span>
            </button>
          </footer>
        </section>
      )}
    </>
  )
}
